import {
  queryOptions,
  useMutation,
  usePrefetchQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { tagQueryKeys, Tags } from '#api';
import type { TagName } from '#dn-types/host.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';

/**
 * Fetches all tags, paginating until we run out of tags
 */
export const maxTagsQueryOptions = queryOptions({
  queryKey: tagQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(Tags.getTags),
});

export function usePrefetchMaxTagsQuery() {
  usePrefetchQuery(maxTagsQueryOptions);
}

export function useSuspenseMaxTagsQuery() {
  return useSuspenseQuery(maxTagsQueryOptions);
}

export function useMaxTagNamesQuery() {
  return useSuspenseQuery({
    ...maxTagsQueryOptions,
    select({ data, metadata }) {
      return {
        data: data.map(({ name }) => name),
        metadata,
      };
    },
  });
}

export const makeTagQueryOptions = (tagName: TagName) =>
  queryOptions({
    queryKey: tagQueryKeys.detail(tagName),
    queryFn: ({ signal }) => Tags.getTag(tagName, { signal }),
  });

/** {@link tagsCountQueryOptions} returns the total number of hosts on the network */
export const tagsCountQueryOptions = queryOptions({
  queryKey: ['tagsCount'],
  queryFn: async ({ signal }) => (await Tags.getTags({ pageSize: 1, init: { signal } })).metadata.totalCount,
});

export function usePrefetchTagsCount() {
  usePrefetchQuery(tagsCountQueryOptions);
}

export function useSuspenseTagQuery(tagName: TagName) {
  // NOTE: we intentionally do NOT seed this from the tags list cache as `initialData`. The list
  // endpoint (GET /v2/tags) omits a tag's firewall rules — it only returns `firewallRulesCount` —
  // whereas the detail endpoint (GET /v1/tags/:name) includes them. Seeding the edit form from a
  // list entry would mount react-hook-form with an empty `firewallRules` even when the tag has some,
  // and RHF won't pick up the later detail fetch, so saving would silently drop the existing rules.
  // Loading straight from the detail query keeps the form authoritative.
  return useSuspenseQuery(makeTagQueryOptions(tagName));
}

/**
 * Creates multiple concurrent suspense requests for tag details. Unlike {@link useSuspenseTagsQuery}
 * (which reads from the list), this hits the detail endpoint per tag, so the results include each
 * tag's firewall rules (the list endpoint omits them).
 */
export function useSuspenseTagsDetailsQueries(tagNames: TagName[]) {
  return useSuspenseQueries({
    queries: tagNames.map((tagName) => makeTagQueryOptions(tagName)),
  });
}

/** {@link useSuspenseTagsQuery} allows fetching a list of tags, filtered from the max tags query */
export function useSuspenseTagsQuery(tagNames: TagName[]) {
  return useSuspenseQuery({
    ...maxTagsQueryOptions,
    select({ data }) {
      const tags = data.filter(({ name }) => tagNames.includes(name));
      return { data: tags };
    },
  });
}

export function useEditTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: Tags.editTag,
    async onSuccess(tag) {
      queryClient.setQueryData(makeTagQueryOptions(tag.name).queryKey, { data: tag });
      // Invalidate all details requests, because the priority could have changed on all of them
      void queryClient.invalidateQueries({ queryKey: tagQueryKeys.details() });
      // Refetch the list before the mutation settles: the reorder UI swaps from its optimistic
      // order to the cached list the moment the save resolves, and the max-tags query has no
      // observer on the edit page, so refetchType 'active' would skip it. Invalidate rather than
      // prefetch so a refetch aborted by the post-save navigation still leaves the cache marked
      // stale for the list page to refetch on mount, instead of looking fresh with pre-edit data.
      await queryClient.invalidateQueries({ queryKey: tagQueryKeys.lists(), refetchType: 'all' });
    },
  });
}

export function useAddTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: Tags.addTag,
    async onSuccess(tag, variables) {
      queryClient.setQueryData(makeTagQueryOptions(tag.name).queryKey, { data: tag });
      // A positional insert shifts other tags' priorities, so invalidate their detail queries —
      // except the new tag's, which was just seeded from the POST response
      if (variables.before ?? variables.after) {
        void queryClient.invalidateQueries({
          queryKey: tagQueryKeys.details(),
          predicate: ({ queryKey }) => queryKey.at(-1) !== tag.name,
        });
      }
      // We refetch during invalidation so that the subscriptions are correct in the route form when it opens
      await queryClient.invalidateQueries({ queryKey: tagQueryKeys.maxList, refetchType: 'all' });
    },
  });
}
